import { captureException } from "@sentry/core";
import type { JwtLoginResponse, RecordType, RefreshResponse, SessionIdResponse, TokenStorage } from "../types/api";
import type { User } from "../types/models";
import { API_BASE } from "./config";
import { ApiError } from "./errors";
import { createActionsApi } from "./resources/actions";
import { createAddressesApi } from "./resources/addresses";
import { createAnalyticsApi } from "./resources/analytics";
import { createBanksApi } from "./resources/banks";
import { createInvoicesApi } from "./resources/invoices";
import { createMeterReadingsApi } from "./resources/meterReadings";
import { createPaymentLogsApi } from "./resources/paymentLogs";
import { createProvidersApi } from "./resources/providers";
import { createScrapingScheduleApi } from "./resources/scrapingSchedule";
import { createSetupApi } from "./resources/setup";

class ApiClient {
  _storage: TokenStorage | null = null;
  _onAuthFailure: (() => void) | null = null;

  setStorage(storage: TokenStorage) {
    this._storage = storage;
  }

  onAuthFailure(callback: () => void) {
    this._onAuthFailure = callback;
  }

  private refreshAuth = async (refreshToken: string): Promise<RefreshResponse> => {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) throw ApiError(res.status, "Failed to refresh token");

    return res.json();
  };

  private _performFetch = async (path: string, options: RequestInit): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const token = (await this._storage?.getToken()) ?? null;
    if (token) headers["Authorization"] = `Bearer ${token}`;

    return fetch(`${API_BASE}/api${path}`, {
      ...options,
      credentials: "include",
      headers: {
        ...headers,
        ...(options.headers as Record<string, string> | undefined),
      },
    });
  };

  private request = async <T>(path: string, options: RequestInit = {}, parse: (res: Response) => Promise<T> = (res) => res.json()): Promise<T> => {
    const res = await this._fetchWithRetry(path, options);
    return this._parseResponse(res, path, parse);
  };

  private _parseResponse = async <T>(res: Response, path: string, parse: (res: Response) => Promise<T> = (res) => res.json()): Promise<T> => {
    if (res.ok) return parse(res);

    const body = await this._parseErrorBody(res);
    const error = ApiError(
      res.status,
      (body.error as string) || (body.message as string) || `Request failed (${res.status} ${res.statusText})`,
      (body.details as RecordType) || undefined,
    );
    captureException(error, {
      tags: { apiPath: path, apiError: "true" },
      extra: { status: res.status },
    });
    throw error;
  };

  private _fetchWithRetry = async (path: string, options: RequestInit): Promise<Response> => {
    const res = await this._performFetch(path, options);
    if (res.status !== 401) return res;

    const refreshed = await this._tryRefresh();
    if (refreshed) return this._performFetch(path, options);
    return res;
  };

  private _tryRefresh = async (): Promise<boolean> => {
    const refreshToken = (await this._storage?.getRefreshToken()) ?? null;
    if (!refreshToken) return false;

    try {
      const refreshed = await this.refreshAuth(refreshToken);
      await this._storage?.setTokens(refreshed.token, refreshed.refreshToken);
      return true;
    } catch {
      await this._storage?.clearTokens();
      this._onAuthFailure?.();
      throw ApiError(401, "Session expired");
    }
  };

  private _parseErrorBody = async (res: Response): Promise<RecordType> => {
    try {
      return await res.json();
    } catch {
      console.error(await res.text().catch(() => ""));
      return {};
    }
  };

  auth = {
    login: (email: string, password: string) =>
      this.request<User>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),

    loginJwt: async (email: string, password: string): Promise<JwtLoginResponse> => {
      const res = await this.request<JwtLoginResponse>("/auth/login-jwt", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      await this._storage?.setTokens(res.token, res.refreshToken);
      return res;
    },

    register: (email: string, password: string, name: string) =>
      this.request<User>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password, name }),
      }),

    logout: async () => {
      try {
        return await this.request<void>("/auth/logout", { method: "POST" });
      } finally {
        await this._storage?.clearTokens();
      }
    },

    me: () => this.request<User>("/auth/me"),
    session: () => this.request<SessionIdResponse>("/auth/session"),
  };

  invoices = createInvoicesApi(this.request);
  providers = createProvidersApi(this.request);
  addresses = createAddressesApi(this.request);
  actions = createActionsApi(this.request);
  analytics = createAnalyticsApi(this.request);
  paymentLogs = createPaymentLogsApi(this.request);
  banks = createBanksApi(this.request);
  scrapingSchedule = createScrapingScheduleApi(this.request);
  setup = createSetupApi(this.request);
  meterReadings = createMeterReadingsApi(this.request);
}

const api = new ApiClient();

export default api;
